import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  #activeFetch;
  #requestVersion = 0;
  #cachedRecommendations = new Map();
  #mutationObserver = new MutationObserver(() => {
    this.dataset.recommendationsPerformed = 'false';
    this.#loadRecommendations();
  });

  connectedCallback() {
    super.connectedCallback();
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-id', 'data-section-id', 'data-intent', 'data-url'],
    });
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
    this.#requestVersion++;
  }

  async #loadRecommendations() {
    if (this.dataset.recommendationsPerformed === 'true') return;
    const { productId, sectionId, intent, url: baseUrl } = this.dataset;
    if (!productId || !sectionId || !baseUrl) {
      this.#hideRecommendations();
      return;
    }
    const version = ++this.#requestVersion;
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('intent', intent || 'related');
      let html = this.#cachedRecommendations.get(url.href);
      if (!html) {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        html = await response.text();
        this.#cachedRecommendations.set(url.href, html);
      }
      if (version !== this.#requestVersion || !this.isConnected) return;
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const recommendation = fragment.getElementById(this.id);
      if (!recommendation?.querySelector('[data-has-recommendations="true"] a[href*="/products/"]')) {
        this.#hideRecommendations();
        return;
      }
      this.dataset.recommendationsPerformed = 'true';
      morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      this.hidden = false;
      this.classList.remove('hidden');
    } catch {
      if (version === this.#requestVersion && this.isConnected) this.#hideRecommendations();
    } finally {
      clearTimeout(timeout);
      if (this.#activeFetch === controller) this.#activeFetch = undefined;
    }
  }

  #hideRecommendations() {
    this.hidden = true;
    this.dataset.recommendationsPerformed = 'true';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
